/**
 * The change marks on the replicas chart (TRA-1558 step 2): one dot on the
 * time line for each saved replica setting change inside the stretch the
 * chart shows, and the words its hover carries. No DOM, so testable as is.
 */

import type { ReplicaSettingChangeResponse } from '@/hooks/useModels'

import type { TimeRange } from './replicaZoom'
import { changeAuthor, formatChangeTime } from './scheduleWindows'

export interface ChangeMark {
  id: string
  epoch: number
  /** Three lines: when (Pacific), who, what changed. */
  title: string
}

/** 'Oct 21, 6:52 AM PT', then who, then the summary, one per line. */
export function markTitle(change: ReplicaSettingChangeResponse): string {
  return [`${formatChangeTime(change.changed_at)} PT`, changeAuthor(change), change.summary].join('\n')
}

/**
 * Whether the read may have left out changes the stretch shown would mark:
 * the route gave its whole cap (`limit` rows, newest first) and the oldest
 * of them is still inside `span`, so older ones in the stretch were cut.
 */
export function marksCapped(
  changes: readonly ReplicaSettingChangeResponse[],
  limit: number,
  span: TimeRange,
): boolean {
  if (changes.length < limit) return false
  const oldest = Math.min(...changes.map((change) => new Date(change.changed_at).getTime()))
  return oldest > span.start
}

/** The changes inside `span` (edges included), oldest first, as marks. */
export function changeMarks(
  changes: readonly ReplicaSettingChangeResponse[],
  span: TimeRange,
): ChangeMark[] {
  return changes
    .flatMap((change) => {
      const epoch = new Date(change.changed_at).getTime()
      if (Number.isNaN(epoch) || epoch < span.start || epoch > span.end) return []
      return [{ id: change.id, epoch, title: markTitle(change) }]
    })
    .sort((a, b) => a.epoch - b.epoch)
}
